import HelpFeedback from "../help-feedback-popover";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useEffect, useState } from "react";
import { getProfilePhoto } from "../utils/indexedDB";

export default function Sidebar({
  open,
  setOpen,
  section,
  setSection,
  hoveredMenu,
  setHoveredMenu,
  sidebarTheme,
  SIDEBAR_THEME,
  styles,
  isAuthenticated,
  userProfile,
}) {
  const isMobile = window.innerWidth <= 600;

  if (!open) return null;

  const [photo, setPhoto] = useState(null);
  const isGuest = !isAuthenticated;

  const displayName = isGuest
    ? "Guest"
    : userProfile?.name ||
      userProfile?.Name ||
      auth.currentUser?.displayName ||
      auth.currentUser?.email?.split("@")[0] ||
      "User";

  const displayPhoto = isGuest ? null : photo;

  const LOCKED = ["history", "analysis", "settings"];
  const user = auth.currentUser;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setPhoto(null); // ✅ only once, AFTER logout
      alert("You have been signed out successfully.");
      setSection("login");
      setOpen(false);
    } catch (e) {
      console.error("Logout failed", e);
    }
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    let url = null;

    if (!isAuthenticated) {
      setPhoto(null);
      return;
    }

    (async () => {
      const blob = await getProfilePhoto();
      if (blob && active) {
        url = URL.createObjectURL(blob);
        setPhoto(url);
      }
    })();

    return () => {
      active = false;
      if (url) {
        URL.revokeObjectURL(url); // 🔥 MEMORY LEAK FIX
      }
    };
  }, [isAuthenticated, open]);

  /* ================= MENU ITEM ================= */
  const menu = (label, key) => {
    const isActive = section === key;
    const isHovered = hoveredMenu === key;
    const isLocked = !isAuthenticated && LOCKED.includes(key);

    return (
      <button
        onClick={() => {
          if (isLocked) setSection("login");
          else setSection(key);
          setOpen(false);
        }}
        onMouseEnter={() => setHoveredMenu(key)}
        onMouseLeave={() => setHoveredMenu(null)}
        style={{
          ...styles(sidebarTheme).menuItem,
          minHeight: isMobile ? 48 : undefined,

          justifyContent: "flex-start",
          opacity: isLocked ? 0.45 : 1,
          cursor: isLocked ? "not-allowed" : "pointer",
          background:
            isHovered || isActive
              ? "linear-gradient(135deg, rgba(124,58,237,0.75), rgba(147,51,234,0.75))"
              : "transparent",
          color: sidebarTheme.text,
          position: "relative",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: 12,
            top: "50%",
            transform: "translateY(-50%)",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#fff",
            opacity: isHovered || isActive ? 1 : 0,
            transition: "0.2s",
          }}
        />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{label}</span>
          {isLocked && (
            <span style={{ fontSize: 11, opacity: 0.7 }}>Login required</span>
          )}
        </div>
      </button>
    );
  };

  /* ================= SECTION HEADER ================= */
  const sectionBlock = (title, subtitle) => (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          margin: "28px 0 6px",
        }}
      >
        <span
          style={{
            width: 5,
            height: 18,
            borderRadius: 4,
            background: sidebarTheme.accent,
          }}
        />
        <span
          style={{
            fontSize: 22,
            fontWeight: 900,
            letterSpacing: "0.6px",
            color: sidebarTheme.accent,
          }}
        >
          {title}
        </span>
      </div>

      <div
        style={{
          width: "100%",
          borderBottom: `1px dotted ${
            sidebarTheme === SIDEBAR_THEME.dark
              ? "rgba(148,163,184,0.45)"
              : "rgba(15,23,42,0.45)"
          }`,
          margin: "10px 0",
        }}
      />

      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: "0.6px",
          marginBottom: 10,
          opacity: 0.85,
        }}
      >
        {subtitle}
      </div>
    </>
  );

  /* ================= RENDER ================= */
  return (
    <div
      style={{
        ...styles(sidebarTheme).overlay,
        alignItems: isMobile ? "flex-start" : "stretch",
      }}
      onClick={() => setOpen(false)}
    >
      <div
        style={{
          ...styles(sidebarTheme).sidebar,
          width: isMobile ? "82%" : styles(sidebarTheme).sidebar.width,
          maxWidth: isMobile ? 320 : styles(sidebarTheme).sidebar.maxWidth,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 60,
          }}
        >
          {/* DASHBOARD */}
          <div
            style={{
              fontSize: isMobile
                ? "clamp(28px, 10vw, 42px)"
                : "clamp(36px, 8vw, 90px)",

              fontWeight: 900,
              marginTop: 1,
              marginBottom: isMobile ? 20 : 40,
              lineHeight: 1.15,
              background: "linear-gradient(90deg, #7c3aed, #ec4899)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            Dashboard
          </div>

          <div
            onClick={() => {
              if (!isGuest) {
                setSection("profile");
                setOpen(false);
              }
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: isMobile ? "6px 0px 24px" : "10px 0px 80px",

              cursor: isGuest ? "default" : "pointer",
            }}
          >
            <div
              style={{
                borderRadius: "50%",
                background: displayPhoto
                  ? `url(${displayPhoto}) center/cover`
                  : "linear-gradient(135deg,#7c3aed,#9333ea)",
                boxShadow: "0 6px 18px rgba(124,58,237,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: 900,
                width: isMobile ? 64 : 100,
                height: isMobile ? 64 : 100,
                fontSize: isMobile ? 28 : 36,
              }}
            >
              {!displayPhoto && displayName.charAt(0)}
            </div>

            <div
              style={{
                marginTop: 1,
                fontSize: isMobile
                  ? "clamp(18px, 6vw, 26px)"
                  : "clamp(22px, 6vw, 45px)",

                fontWeight: 900,
                letterSpacing: "0.9px",
                background: "linear-gradient(90deg, #7c3aed, #ec4899)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {displayName}
            </div>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                fontWeight: 600,
                opacity: 0.7,
              }}
            >
              {isGuest ? "Guest user" : "View & edit"}
            </div>
          </div>

          {menu("Start Practice", "practice")}

          {sectionBlock("PERSONAL", "PROFILE & PERFORMANCE")}
          {menu("Your Profile", "profile")}
          {menu("Test History", "history")}
          {menu("Analysis", "analysis")}

          {sectionBlock("APP", "SETTINGS & DATA")}
          {menu("Settings", "settings")}
          {menu("Reset", "reset")}

          {sectionBlock("LEARNING", "HELP & WALKTHROUGHS")}
          {menu("📘 Guides & Tutorials", "guides")}

          {sectionBlock("ACCOUNT", "AUTHENTICATION")}

          {isAuthenticated ? (
            <>
              <div
                style={{
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: `1px solid ${sidebarTheme.border}`,
                  marginBottom: 10,
                  fontSize: 13,
                  opacity: 0.85,
                }}
              >
                Signed in as
                <div style={{ fontWeight: 700, marginTop: 4 }}>
                  {user?.email}
                </div>
              </div>

              <button
                onClick={handleLogout}
                style={{
                  ...styles(sidebarTheme).menuItem,
                  minHeight: isMobile ? 48 : undefined,

                  justifyContent: "center",
                  background: sidebarTheme.danger,
                  color: "#fff",
                }}
              >
                Logout
              </button>
            </>
          ) : (
            menu("🔐 Sign In / Login", "login")
          )}

          {sectionBlock("COMPANY", "LEGAL & INFORMATION")}
          {menu("📄 Privacy Policy", "privacy")}
          {menu("📜 Terms of Service", "terms")}
          {menu("💳 Refund Policy", "refund")}
          {menu("📞 Contact", "contact")}
        </div>

        <div
          style={{
            position: "sticky",
            bottom: 0,
            paddingTop: 12,
            paddingBottom: 12,
            background: sidebarTheme.bgMain,
            borderTop: `1px solid ${sidebarTheme.border}`,
          }}
        >
          <HelpFeedback sidebarTheme={sidebarTheme} />
        </div>
      </div>
    </div>
  );
}
